import {
  AionzSnackbar,
  ExclusiveSidePanelService,
  LoadingService,
  SidePanelComponent
} from '@aionzdev/design-system';
import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { auditTime, filter, finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { WebsocketService } from '@aionzdev/modules';
import { LoadState } from '../../../enums';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { PurchaseRequestAuditDto } from '../../../interfaces/purchase-request-audit.dto';
import { PurchaseRequestCommentDto } from '../../../interfaces/purchase-request-comment.dto';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { PurchaseRequestActivitiesTabComponent } from '../purchase-request-activities-tab/purchase-request-activities-tab.component';
import {
  PurchaseRequestCommentSubmit,
  PurchaseRequestCommentsTabComponent
} from '../purchase-request-comments-tab/purchase-request-comments-tab.component';

const PANEL_ID = 'purchase-request-activity';

@Component({
  selector: 'aionz-purchase-request-activity-panel',
  imports: [
    MatSidenavModule,
    MatTabsModule,
    SidePanelComponent,
    PurchaseRequestActivitiesTabComponent,
    PurchaseRequestCommentsTabComponent
  ],
  templateUrl: './purchase-request-activity-panel.component.html',
  styleUrl: './purchase-request-activity-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestActivityPanelComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly snackBar = inject(AionzSnackbar);
  private readonly loadingService = inject(LoadingService);
  private readonly exclusiveSidePanel = inject(ExclusiveSidePanelService);
  private readonly websocketService = inject(WebsocketService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly websocketUpdate$ = new Subject<void>();
  private realtimeDestroy$ = new Subject<void>();

  readonly purchaseId = input.required<string>();
  readonly panelTitle = input('Atividade da solicitação');
  readonly panelSubtitle = input<string>();

  readonly activityChanged = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly activeSidebarTabIndex = signal(0);
  protected readonly LoadState = LoadState;

  protected readonly commentsData = signal<PurchaseRequestCommentDto[]>([]);
  protected readonly commentsLoadState = signal<LoadState>(LoadState.IDLE);
  protected readonly commentsInitialized = signal(false);
  protected readonly isCommentsSubmitting = signal(false);

  protected readonly activitiesData = signal<PurchaseRequestAuditDto[]>([]);
  protected readonly activitiesLoadState = signal<LoadState>(LoadState.IDLE);
  protected readonly activitiesInitialized = signal(false);

  /** Público: a página usa no badge do botão de comentários. `null` enquanto não se sabe. */
  readonly commentsCount = computed(() => {
    const initialized = this.commentsInitialized();
    return initialized ? this.commentsData().length : null;
  });

  protected readonly commentsTabLabel = computed(() => {
    const count = this.commentsCount();
    return count !== null ? `Comentários (${count})` : 'Comentários';
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (isPlatformBrowser(this.platformId)) {
        const config = this.purchasingService.getRealtimeConfig();
        this.websocketService.sendMessage(config.leaveEvent, config.tenantId);
      }
      this.realtimeDestroy$.next();
      this.realtimeDestroy$.complete();
    });

    effect(() => {
      const id = this.purchaseId();

      untracked(() => {
        this.commentsInitialized.set(false);
        this.commentsData.set([]);
        this.commentsLoadState.set(LoadState.IDLE);
        this.activitiesInitialized.set(false);
        this.activitiesData.set([]);
        this.activitiesLoadState.set(LoadState.IDLE);

        if (isPlatformBrowser(this.platformId) && id) {
          this.setupRealtimeUpdates();
          this.preloadComments();
        }

        if (this.isOpen()) {
          this.loadActiveTab();
        }
      });
    });

    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.exclusiveSidePanel.announceOpen(PANEL_ID));
      }
    });

    this.exclusiveSidePanel.opened$
      .pipe(
        filter(id => id !== PANEL_ID),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.close());
  }

  private setupRealtimeUpdates(): void {
    this.realtimeDestroy$.next();
    const purchaseId = this.purchaseId();
    if (!purchaseId) return;

    const config = this.purchasingService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.activity);

    this.websocketUpdate$
      .pipe(auditTime(500), takeUntil(this.realtimeDestroy$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const activeIndex = this.activeSidebarTabIndex();
        const canRefreshInPlace = this.isOpen();

        if (activeIndex === 0 && canRefreshInPlace) {
          this.loadActivities(true);
        } else {
          this.activitiesInitialized.set(false);
        }

        if (activeIndex === 1 && canRefreshInPlace) {
          this.loadComments(true);
        } else {
          // mantém o badge da página atualizado sem abrir a aba
          this.preloadComments();
        }
      });

    this.websocketService.sendMessage(config.joinEvent, config.tenantId);

    this.websocketService.listen(config.eventName, (event: any) => {
      const mappedEvent: PurchasingEvent = this.purchasingService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        mappedEvent.purchaseId === purchaseId &&
        updateTypes.has(mappedEvent.type)
      ) {
        this.websocketUpdate$.next();
      }
    });
  }

  open(): void {
    this.isOpen.set(true);
    this.loadActiveTab();
  }

  /** Abre o painel já na aba pedida (0 = atividade, 1 = comentários). */
  openTab(index: 0 | 1): void {
    this.activeSidebarTabIndex.set(index);
    this.open();
  }

  /**
   * Carrega os comentários em segundo plano só para a contagem: não muda o estado de
   * carregamento da aba e ignora erro, que será tratado quando a aba for aberta de fato.
   */
  private preloadComments(): void {
    const purchaseId = this.purchaseId();
    if (!purchaseId || this.commentsLoadState() === LoadState.LOADING) return;
    this.purchasingService
      // em segundo plano: um erro da API não deve abrir o dialog global de erro
      .getPurchaseRequestComments(purchaseId, { hideGlobalError: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.commentsData.set(response.body || []);
          this.commentsInitialized.set(true);
        },
        error: () => {
          /* silencioso: o badge fica oculto até a aba carregar */
        }
      });
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.update(open => !open);
    if (this.isOpen()) {
      this.loadActiveTab();
    }
  }

  reloadActivities(): void {
    this.activitiesInitialized.set(false);
    if (this.isOpen() && this.activeSidebarTabIndex() === 0) {
      this.loadActivities();
    }
  }

  protected onSidebarTabChange(event: MatTabChangeEvent): void {
    this.activeSidebarTabIndex.set(event.index);
    if (event.index === 0) {
      this.loadActivities();
    } else if (event.index === 1) {
      this.loadComments();
    }
  }

  protected handleCommentSubmit({ text, file }: PurchaseRequestCommentSubmit): void {
    if (this.isCommentsSubmitting()) {
      return;
    }

    this.isCommentsSubmitting.set(true);
    this.loadingService.showLoading();

    const purchaseId = this.purchaseId();
    const fileUrl$ = file
      ? this.purchasingService
          .sendPurchaseRequestCommentFile(purchaseId, file)
          .pipe(map(response => response.body ?? undefined))
      : of(undefined);

    fileUrl$
      .pipe(
        switchMap(fileUrl => this.purchasingService.createPurchaseRequestComment(purchaseId, text, fileUrl)),
        finalize(() => {
          this.isCommentsSubmitting.set(false);
          this.loadingService.hideLoading();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (response.body) {
            this.commentsData.update(comments => [...comments, response.body!]);
            this.snackBar.success('Comentário enviado com sucesso!');
            this.reloadActivities();
            this.activityChanged.emit();
          }
        },
        error: () => {
          this.snackBar.error('Erro ao enviar comentário. Tente novamente.');
        }
      });
  }

  private loadActiveTab(): void {
    if (this.activeSidebarTabIndex() === 0) {
      this.loadActivities();
    } else if (this.activeSidebarTabIndex() === 1) {
      this.loadComments();
    }
  }


  private loadComments(silent = false): void {
    if (this.commentsLoadState() === LoadState.LOADING) {
      return;
    }
    if (this.commentsInitialized() && !silent) {
      return;
    }
    if (!silent) {
      this.commentsLoadState.set(LoadState.LOADING);
    }

    this.purchasingService.getPurchaseRequestComments(this.purchaseId())
      .pipe(
        finalize(() => {
          if (this.commentsLoadState() === LoadState.LOADING) {
            this.commentsLoadState.set(LoadState.LOADED);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.commentsData.set(response.body || []);
          this.commentsInitialized.set(true);
        },
        error: () => {
          if (!silent) {
            this.commentsLoadState.set(LoadState.ERROR);
          }
          this.snackBar.error('Erro ao carregar comentários da solicitação.');
        }
      });
  }

  private loadActivities(silent = false): void {
    if (this.activitiesLoadState() === LoadState.LOADING) {
      return;
    }
    if (this.activitiesInitialized() && !silent) {
      return;
    }
    if (!silent) {
      this.activitiesLoadState.set(LoadState.LOADING);
    }

    this.purchasingService.getPurchaseRequestAudits({ purchaseId: this.purchaseId() })
      .pipe(
        finalize(() => {
          if (this.activitiesLoadState() === LoadState.LOADING) {
            this.activitiesLoadState.set(LoadState.LOADED);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.activitiesData.set(response.body || []);
          this.activitiesInitialized.set(true);
        },
        error: () => {
          if (!silent) {
            this.activitiesLoadState.set(LoadState.ERROR);
          }
          this.snackBar.error('Erro ao carregar atividades da solicitação.');
        }
      });
  }
}
